// Sub-componente: destinatários da NF da parcela (Sprint F8; desde 2026-09-01
// mora na aba "🧾 Dados pra NF" do ModalParcela; desde 21/09/2026 no TOPO dela).
//
// Reforma de 21/09/2026 (Lucas): "quando eu tenho vários e-mails, ele fica
// escondido… Eu não deveria precisar salvar destinatários dentro do modal que
// também é salvo." Duas mudanças:
//   1. Quem recebe aparece numa FILA DE ETIQUETAS sempre à vista (nome + e-mail,
//      × para tirar, ⧉ para copiar todos). Antes o contato escolhido era só uma
//      caixinha marcada no meio de uma lista que rola, e o resumo dizia apenas
//      "N destinatário(s)".
//   2. O componente é CONTROLADO: a lista E o texto digitado moram no
//      ModalParcela e vão no MESMO "Salvar alterações" do rodapé
//      (montarPayloadDestinatarios, parcelaForm.ts). O botão "Salvar
//      destinatários" saiu.
//
// Estrutura gravada em parcela.emails_nf_destinatarios (jsonb):
//   [{ tipo: 'contato', id: 'uuid_do_contato' },
//    { tipo: 'email',   email: 'avulso@x.com' }]
// Toda a régua (incluir, tirar, repetido, colar vários) mora em parcelaForm.ts,
// com testes no CI — aqui fica só o desenho.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Select } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { inputMP } from './utils';
import {
  consolidarDestinatarios, emailsParaCopiar, incluirContato, incluirAvulsos,
  removerDestinatario,
} from './parcelaForm';
import type { Destinatario } from './parcelaForm';
import type { ContatoCliente } from './types';

const ehFinanceiro = (papel?: string | null) => papel === 'financeiro' || papel === 'ambos';
type Contato = ContatoCliente & { doContratante?: boolean };

export function EmailsNfParcela({
  clienteId, contratanteId, clienteNome, valor, sugerido, legado, alterado,
  estadoDetalhe, onChange, digitado, onDigitado, onContatos,
}: {
  // Quem recebe a NF: o "Faturar para" da parcela ou, em branco, o contratante.
  clienteId: number | null;
  // Contratante do contrato — quando a nota sai para OUTRO CNPJ, os contatos
  // dele também são oferecidos (em 14 de 19 parcelas faturadas para outro CNPJ
  // quem recebe a nota não tinha contato nenhum; o contratante tinha).
  contratanteId?: number | null;
  clienteNome?: string | null;
  valor: Destinatario[];
  // A lista veio do campo antigo "E-mail de faturamento" e ninguém decidiu ainda.
  sugerido: boolean;
  // Texto do campo antigo, quando ele foi oferecido ou tem algo que não virou
  // e-mail — mostrado inteiro para ninguém perder endereço.
  legado?: { texto: string; naoLidos: string[] } | null;
  // Mexida e ainda não salva (mostra o aviso "falta salvar").
  alterado: boolean;
  // A listagem do CR NÃO traz emails_nf_destinatarios — só o detalhe. Sem ele,
  // editar seria editar uma lista vazia que não é a real.
  estadoDetalhe: 'carregando' | 'ok' | 'falhou';
  onChange: (lista: Destinatario[]) => void;
  // O campo de digitar é do MODAL: o texto sobrevive à troca de aba e o
  // "Salvar alterações" enxerga o que ficou sem incluir.
  digitado: string;
  onDigitado: (texto: string) => void;
  // Os contatos lidos, para o Salvar do modal incluir o texto pendente como
  // CONTATO quando o e-mail é de um (mesma régua do Enter).
  onContatos?: (contatos: ContatoCliente[]) => void;
}) {
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);
  // Falha ao ler os contatos NÃO é "cliente sem contato": sem este estado, toda
  // etiqueta de contato virava "removido" e o Copiar saía incompleto calado.
  const [contatosFalhou, setContatosFalhou] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const ids = Array.from(new Set([clienteId, contratanteId].filter((x): x is number => !!x)));
      if (!ids.length) { setContatos([]); setContatosFalhou(false); setLoading(false); return; }
      setLoading(true);
      try {
        const token = await getToken();
        const listas = await Promise.all(ids.map(async id => {
          const r = await axios.get(`${API_URL}/comercial/clientes/${id}/contatos`,
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          return (Array.isArray(d) ? d : []).map((c: ContatoCliente) =>
            ({ ...c, doContratante: id !== clienteId }));
        }));
        const vistos = new Set<string>();
        const juntos: Contato[] = [];
        for (const c of listas.flat()) {
          if (vistos.has(c.id)) continue;
          vistos.add(c.id); juntos.push(c);
        }
        if (vivo) { setContatos(juntos); setContatosFalhou(false); onContatos?.(juntos); }
      } catch {
        if (vivo) { setContatos([]); setContatosFalhou(true); onContatos?.([]); }
      } finally { if (vivo) setLoading(false); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, contratanteId, tentativa]);

  const itens = useMemo(() => consolidarDestinatarios(valor, contatos), [valor, contatos]);

  // Contatos que ainda dá para incluir: os de quem recebe a nota primeiro,
  // financeiro/ambos antes; sem e-mail aparece desabilitado.
  const opcoes = useMemo(() => {
    const escolhidos = new Set(valor.filter(d => d.tipo === 'contato').map(d => d.id));
    return contatos
      .filter(c => !escolhidos.has(c.id))
      .sort((a, b) => Number(!!a.doContratante) - Number(!!b.doContratante)
        || Number(ehFinanceiro(b.papel)) - Number(ehFinanceiro(a.papel))
        || a.nome.localeCompare(b.nome, 'pt-BR'))
      .map(c => ({
        value: c.id,
        disabled: !c.email,
        doContratante: !!c.doContratante,
        label: `${c.nome} — ${c.email || 'sem e-mail no cadastro'}`
          + (ehFinanceiro(c.papel) ? ' · financeiro' : '')
          + (c.cargo ? ` · ${c.cargo}` : '')
          + (c.doContratante ? ' · do contratante' : ''),
      }));
  }, [contatos, valor]);
  const nEscolhiveis = opcoes.filter(o => !o.disabled).length;
  // Contagem separada: "de <cliente>" não pode contar os do contratante.
  const nDoContratante = opcoes.filter(o => !o.disabled && o.doContratante).length;
  const nDoCliente = nEscolhiveis - nDoContratante;

  function escolherContato(id: string) {
    const c = contatos.find(x => x.id === id);
    if (!c) return;
    const r = incluirContato(valor, c);
    setAviso(r.aviso || null);
    if (r.lista !== valor) onChange(r.lista);
  }

  // Enter e "+ Incluir" incluem; o que não é e-mail válido FICA no campo para
  // corrigir. Quem digita e vai direto no Salvar do rodapé é coberto LÁ (o
  // Salvar inclui o texto pendente). Incluir ao SAIR do campo foi tentado e
  // descartado (2ª revisão de 21/09): o aviso do campo antigo sumia no
  // mousedown, o rodapé subia, o clique caía no fundo e abria "descartar?"; e
  // trocar de janela no meio da digitação incluía "fulano@empresa.com" sem o
  // ".br".
  function adicionarDigitados() {
    if (!digitado.trim()) return;
    const r = incluirAvulsos(valor, contatos, digitado);
    if (r.incluidos) onChange(r.lista);
    setAviso(r.recusados.length ? 'Não entrou: ' + r.recusados.join('; ') : null);
    onDigitado(r.restante);
  }

  const temContatoNaLista = valor.some(d => d.tipo === 'contato');
  const copiaIncompleta = contatosFalhou && temContatoNaLista;
  async function copiarTodos() {
    const txt = emailsParaCopiar(itens);
    if (!txt || copiaIncompleta) return;
    try {
      await navigator.clipboard.writeText(txt);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { window.prompt('Copie os e-mails:', txt); }
  }

  if (estadoDetalhe === 'carregando' || loading) {
    return <div style={{ padding: 12, color: '#888', fontSize: 12 }}>Carregando destinatários…</div>;
  }
  if (estadoDetalhe === 'falhou') {
    return (
      <div style={{ fontSize: 11.5, color: '#7A5500', padding: '10px 12px',
        background: '#FFF8E8', border: '0.5px solid #F0D88E', borderRadius: 5 }}>
        ⚠ Os destinatários salvos não carregaram — editar agora poderia apagar a
        lista real. Use o "tentar de novo" no rodapé e volte aqui.
      </div>
    );
  }

  const nCopiaveis = itens.filter(i => i.email).length;
  const finCount = contatos.filter(c => !c.doContratante && ehFinanceiro(c.papel)).length;
  const temContatoDoCliente = contatos.some(c => !c.doContratante);
  const legadoIncompleto = !!legado && legado.naoLidos.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Cabeçalho: quantos, se falta salvar, e copiar todos */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#020D7F' }}>
          📧 Quem recebe esta NF ({itens.length})
        </div>
        {alterado && (
          <span style={{ fontSize: 10.5, padding: '1px 8px', borderRadius: 10,
            background: '#FFF3D8', color: '#7A5500', fontWeight: 600 }}>
            alterado — grava no Salvar alterações
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={copiarTodos} disabled={!nCopiaveis || copiaIncompleta}
          title={copiaIncompleta
            ? 'Os contatos não carregaram — a cópia sairia sem eles'
            : 'Copia os e-mails separados por ; para colar no emissor da nota'}
          style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 600,
            background: '#fff', color: nCopiaveis && !copiaIncompleta ? '#0930D0' : '#aaa',
            border: '0.5px solid #c9d0ea', borderRadius: 4,
            cursor: nCopiaveis && !copiaIncompleta ? 'pointer' : 'default' }}>
          {copiado ? '✓ Copiado' : `⧉ Copiar e-mails${nCopiaveis > 1 ? ` (${nCopiaveis})` : ''}`}
        </button>
      </div>

      {contatosFalhou && (
        <div style={{ fontSize: 11.5, color: '#7A5500', padding: '7px 10px',
          background: '#FFF8E8', border: '0.5px solid #F0D88E', borderRadius: 5 }}>
          ⚠ Não consegui carregar os contatos do cliente — os destinatários salvos
          continuam na lista, só não dá para ver o e-mail deles agora.{' '}
          <button type="button" onClick={() => setTentativa(t => t + 1)}
            style={{ background: 'transparent', border: 'none', color: '#0930D0',
              cursor: 'pointer', fontSize: 11.5, fontWeight: 600, padding: 0,
              textDecoration: 'underline' }}>
            tentar de novo
          </button>
        </div>
      )}

      {(sugerido || legadoIncompleto) && legado && (
        <div style={{ fontSize: 11, color: '#7A5500', padding: '7px 10px',
          background: '#FFF8E8', border: '0.5px solid #F0D88E', borderRadius: 5,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 220 }}>
            ⚠ O campo antigo "E-mail de faturamento" desta parcela diz:{' '}
            <strong style={{ wordBreak: 'break-all' }}>{legado.texto}</strong>.{' '}
            {legadoIncompleto
              ? <>Parte dele não parece e-mail e <strong>não entrou</strong> na lista
                  ({legado.naoLidos.join(', ')}) — confira e digite abaixo se for o caso.
                  O campo antigo fica guardado.</>
              : sugerido
                ? <>Confirme ou tire quem não recebe — a lista só passa a valer no <strong>Salvar alterações</strong>.</>
                : null}
          </span>
          {sugerido && (
            <button type="button" onClick={() => onChange(valor)}
              style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600,
                background: '#fff', color: '#7A5500', border: '0.5px solid #E4C66E',
                borderRadius: 4, cursor: 'pointer' }}>
              ✓ Usar esta lista
            </button>
          )}
        </div>
      )}

      {/* A fila: uma etiqueta por pessoa, todas à vista (quebram linha, sem "+3") */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 30,
        padding: 8, background: '#FAFBFF', border: '0.5px solid #d8dcec', borderRadius: 6 }}>
        {itens.length === 0 && (
          <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic', alignSelf: 'center' }}>
            Ninguém ainda — escolha um contato ou digite um e-mail logo abaixo.
          </span>
        )}
        {itens.map(it => {
          // Contato fora da lista carregada: se a LEITURA falhou é só "não
          // carregou"; se leu e não achou, ele não é deste cliente (o "Faturar
          // para" mudou) ou saiu do cadastro — não afirmar que foi apagado.
          const naoCarregou = it.orfao && contatosFalhou;
          const ambar = (it.orfao && !contatosFalhou) || sugerido;
          return (
            <span key={it.chave}
              title={naoCarregou ? 'Os contatos não carregaram agora'
                : it.orfao ? 'Este contato não está entre os contatos deste cliente (ou saiu do cadastro)'
                : (it.email || '')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                padding: '4px 6px 4px 10px', fontSize: 12, borderRadius: 14,
                background: naoCarregou ? '#F4F4F6' : ambar ? '#FFF3D8' : '#EFF3FF',
                color: naoCarregou ? '#666' : ambar ? '#7A5500' : '#0930D0',
                border: `0.5px solid ${naoCarregou ? '#ddd' : ambar ? '#F0D88E' : '#C9D4FF'}`,
              }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {naoCarregou ? <>contato (não carregou)</>
                  : it.orfao ? <>contato fora deste cliente (ou saiu do cadastro)</>
                  : it.nome ? <><strong>{it.nome}</strong> · {it.email || 'sem e-mail'}</>
                  : it.email}
                {ehFinanceiro(it.papel) && (
                  <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#B07500' }}>FIN</span>
                )}
              </span>
              <button type="button" onClick={() => onChange(removerDestinatario(valor, it.chave))}
                title="Tirar da lista"
                style={{ background: 'transparent', border: 'none', color: 'inherit',
                  cursor: 'pointer', padding: '0 2px', fontSize: 15, lineHeight: 1 }}>×</button>
            </span>
          );
        })}
      </div>

      {/* Incluir: contato do cliente (busca por nome ou e-mail) OU e-mail digitado */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Select
          showSearch optionFilterProp="label"
          value={null as string | null}
          placeholder={nDoCliente
            ? `+ Contato${clienteNome ? ` de ${clienteNome}` : ''} (${nDoCliente} com e-mail`
              + (nDoContratante ? ` + ${nDoContratante} do contratante)` : ')')
            : nDoContratante
            ? `+ Contato do contratante (${nDoContratante} com e-mail) — quem recebe a nota não tem contato`
            : contatos.length ? 'Nenhum contato com e-mail para incluir'
            : contatosFalhou ? 'Contatos não carregaram'
            : clienteId ? 'Este cliente não tem contato cadastrado' : 'Parcela sem cliente'}
          disabled={!nEscolhiveis}
          options={opcoes}
          onChange={(v) => v && escolherContato(v)}
          style={{ flex: '1 1 300px', minWidth: 0 }}
          notFoundContent="Nenhum contato com esse nome ou e-mail"
        />
        <div style={{ display: 'flex', gap: 6, flex: '1 1 300px', minWidth: 0 }}>
          <input type="text" value={digitado}
            onChange={e => onDigitado(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarDigitados(); } }}
            placeholder="ou digite/cole e-mail(s) e tecle Enter"
            style={{ ...inputMP, flex: 1, minWidth: 0 }} />
          <button type="button" onClick={adicionarDigitados} disabled={!digitado.trim()}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
              background: digitado.trim() ? '#0930D0' : '#8FA0E8', color: '#fff',
              border: 'none', borderRadius: 4, cursor: digitado.trim() ? 'pointer' : 'default' }}>
            + Incluir
          </button>
        </div>
      </div>

      {aviso && (
        <div style={{ fontSize: 11, color: '#B07500' }}>⚠ {aviso}</div>
      )}
      {temContatoDoCliente && finCount === 0 && (
        <div style={{ fontSize: 11, color: '#888' }}>
          Nenhum contato deste cliente está marcado como "financeiro" — dá para marcar em
          Comercial → Clientes, e ele passa a aparecer primeiro aqui.
        </div>
      )}
    </div>
  );
}
